import { Link } from "react-router-dom";
import { useEffect, useState } from "react";

/**
 * A small, dismissible banner that surfaces real seasonal urgency only when
 * the occasion is genuinely within 30 days. Goes away after the date.
 *
 * The "order by" date is the day after which we can't promise delivery — gives
 * a 4-day buffer for AI processing + the gift-giver's printing/forwarding.
 */
type Occasion = {
  name: string;
  /** ISO date of the occasion, e.g. 2026-05-10 (Mother's Day) */
  date: string;
  /** ISO date of the latest "order by" date, e.g. 2026-05-06 */
  orderBy: string;
};

const OCCASIONS: Occasion[] = [
  { name: "Mother's Day", date: "2026-05-10", orderBy: "2026-05-06" },
  { name: "Father's Day (AU/UK)", date: "2026-09-06", orderBy: "2026-09-02" },
  { name: "Father's Day (US)", date: "2026-06-21", orderBy: "2026-06-17" },
  { name: "Grandparents' Day", date: "2026-09-13", orderBy: "2026-09-09" },
  { name: "Christmas", date: "2026-12-25", orderBy: "2026-12-21" },
];

function pickActive(now: Date): Occasion | null {
  for (const o of OCCASIONS) {
    const event = new Date(o.date + "T23:59:59");
    if (event.getTime() < now.getTime()) continue;
    const days = Math.ceil((event.getTime() - now.getTime()) / 86400000);
    if (days > 30) continue;
    return o;
  }
  return null;
}

function dismissKeyFor(o: Occasion) {
  return `b2l:dismiss:${o.name}:${o.date}`;
}

export function OccasionBanner() {
  const [active, setActive] = useState<Occasion | null>(null);

  useEffect(() => {
    const o = pickActive(new Date());
    if (!o) return;
    if (typeof localStorage !== "undefined" && localStorage.getItem(dismissKeyFor(o))) return;
    setActive(o);
  }, []);

  if (!active) return null;

  const orderBy = new Date(active.orderBy + "T00:00:00");
  const orderByLabel = orderBy.toLocaleDateString(undefined, { day: "numeric", month: "long" });

  const onDismiss = () => {
    try {
      localStorage.setItem(dismissKeyFor(active), "1");
    } catch {
      /* ignore */
    }
    setActive(null);
  };

  return (
    <div className="bg-rose-50 border-b border-rose-200/70 text-rose-900">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm">
        <span className="font-medium">
          {active.name} is coming.
        </span>
        <span className="text-rose-800/80">
          Order by{" "}
          <strong className="font-semibold">{orderByLabel}</strong>{" "}
          for guaranteed delivery.
        </span>
        <Link
          to="/upload"
          className="underline underline-offset-2 hover:text-rose-700 font-medium whitespace-nowrap"
        >
          Make one →
        </Link>
        <button
          aria-label="Dismiss"
          onClick={onDismiss}
          className="ml-1 text-rose-600/70 hover:text-rose-900 px-1 leading-none text-base"
        >
          ×
        </button>
      </div>
    </div>
  );
}
